import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { ArrowRight, ArrowLeft, Check, Upload, Sparkles, Palette, Shirt, Calendar as CalendarIcon } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const GARMENTS = ["T-Shirt", "Polo", "Hoodie", "Sweatshirt", "Long Sleeve"];
const COLORS = [
  { hex: "#111", name: "Black" }, { hex: "#f5f5f5", name: "White" }, { hex: "#1e3a5f", name: "Navy" }, { hex: "#264d3c", name: "Forest" }, { hex: "#7a1a2a", name: "Cardinal" }, { hex: "#d9c68a", name: "Sand" }, { hex: "#a7c7dc", name: "Light Blue" }, { hex: "#6b3a1a", name: "Brown" },
];
const SIZES = ["S", "M", "L", "XL", "2XL", "3XL"];

export default function CustomDesign() {
  const [step, setStep] = useState(1);
  const [path, setPath] = useState("design-for-me");
  const [garment, setGarment] = useState("T-Shirt");
  const [idea, setIdea] = useState("");
  const [file, setFile] = useState(null);
  const [colors, setColors] = useState(["#111"]);
  const [sizes, setSizes] = useState({});
  const [dateNeeded, setDateNeeded] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const { toast } = useToast();

  const totalShirts = Object.values(sizes).reduce((a, b) => a + (parseInt(b) || 0), 0);

  const toggleColor = (hex) => setColors((prev) => prev.includes(hex) ? prev.filter((c) => c !== hex) : [...prev, hex]);

  const submit = () => {
    const request = { path, garment, idea, fileName: file?.name || null, colors, sizes, totalShirts, dateNeeded, name, email, submittedAt: new Date().toISOString() };
    const existing = JSON.parse(localStorage.getItem("dms_custom_requests") || "[]");
    localStorage.setItem("dms_custom_requests", JSON.stringify([...existing, request]));
    toast({ title: "Request received!", description: "We'll email you a proof within 1 business day." });
    setStep(5);
  };

  const steps = ["Path", "Design", "Colors", "Sizes & Date", "Contact"];

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <section className="max-w-4xl mx-auto px-5 lg:px-8 py-16">
        <p className="kicker text-center">Custom Design</p>
        <h1 className="font-serif text-4xl md:text-5xl text-center text-[color:var(--dms-navy)] mt-4">Let's put your story on a shirt</h1>
        <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />

        {step <= 4 && (
          <div className="mt-10 flex items-center justify-center gap-2 flex-wrap">
            {steps.slice(0, 4).map((s, i) => (
              <div key={s} className="flex items-center">
                <div className={`w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition ${step > i + 1 ? "bg-[color:var(--dms-peach)] text-[color:var(--dms-navy)]" : step === i + 1 ? "bg-[color:var(--dms-navy)] text-white" : "bg-white text-[color:var(--dms-navy)]/40 border border-[color:var(--dms-navy)]/15"}`}>
                  {step > i + 1 ? <Check className="w-4 h-4" /> : i + 1}
                </div>
                <span className={`ml-2 text-xs uppercase tracking-widest font-semibold ${step === i + 1 ? "text-[color:var(--dms-navy)]" : "text-[color:var(--dms-navy)]/50"}`}>{s}</span>
                {i < 3 && <span className="mx-3 w-6 h-[1px] bg-[color:var(--dms-navy)]/15" />}
              </div>
            ))}
          </div>
        )}

        <div className="mt-10 bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-8 md:p-10">
          {step === 1 && (
            <div>
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">How do you want to start?</h2>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { id: "upload", title: "I already have my design", desc: "Upload logo, artwork or photo.", icon: Upload },
                  { id: "design-for-me", title: "Design it for me", desc: "Tell us the idea, we'll create it.", icon: Sparkles },
                ].map((o) => {
                  const Icon = o.icon;
                  const active = path === o.id;
                  return (
                    <button key={o.id} onClick={() => setPath(o.id)} className={`text-left p-6 rounded-2xl border-2 transition ${active ? "border-[color:var(--dms-peach)] bg-[color:var(--dms-peach)]/5" : "border-[color:var(--dms-navy)]/10 hover:border-[color:var(--dms-navy)]/25"}`}>
                      <div className="w-11 h-11 rounded-full bg-[color:var(--dms-peach)] flex items-center justify-center text-[color:var(--dms-navy)]"><Icon className="w-5 h-5" /></div>
                      <h3 className="font-serif text-xl text-[color:var(--dms-navy)] mt-4">{o.title}</h3>
                      <p className="text-sm text-[color:var(--dms-navy)]/70 mt-2">{o.desc}</p>
                    </button>
                  );
                })}
              </div>
              <div className="mt-8">
                <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Garment</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {GARMENTS.map((g) => (
                    <button key={g} onClick={() => setGarment(g)} className={`px-4 py-2 rounded-full border text-sm ${garment === g ? "bg-[color:var(--dms-navy)] text-white border-[color:var(--dms-navy)]" : "bg-white text-[color:var(--dms-navy)] border-[color:var(--dms-navy)]/20 hover:border-[color:var(--dms-navy)]"}`}>{g}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">{path === "upload" ? "Upload your design" : "Describe your idea"}</h2>
              {path === "upload" ? (
                <label className="mt-6 block border-2 border-dashed border-[color:var(--dms-navy)]/20 rounded-2xl p-10 text-center cursor-pointer hover:border-[color:var(--dms-peach)] transition">
                  <Upload className="w-8 h-8 mx-auto text-[color:var(--dms-peach)]" />
                  <p className="mt-3 font-serif text-lg text-[color:var(--dms-navy)]">{file ? file.name : "Drop your file here or click to browse"}</p>
                  <p className="text-xs text-[color:var(--dms-navy)]/60 mt-1">PNG, JPG, PDF or SVG · up to 25MB</p>
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
              ) : null}
              <textarea
                value={idea}
                onChange={(e) => setIdea(e.target.value)}
                rows={6}
                placeholder="Tell us the words, verse, mood or inspiration you have in mind…"
                className="mt-6 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)]"
              />
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Pick your shirt colors</h2>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/60">Mix and match as many as you like — pricing stays the same.</p>
              <div className="mt-6 grid grid-cols-4 sm:grid-cols-8 gap-4">
                {COLORS.map((c) => {
                  const active = colors.includes(c.hex);
                  return (
                    <button key={c.hex} onClick={() => toggleColor(c.hex)} className="flex flex-col items-center gap-2 group">
                      <span className={`w-12 h-12 rounded-full border-2 transition ${active ? "border-[color:var(--dms-peach)] scale-110" : "border-[color:var(--dms-navy)]/15 group-hover:border-[color:var(--dms-navy)]/40"}`} style={{ background: c.hex }} />
                      <span className="text-xs text-[color:var(--dms-navy)]/70">{c.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-serif text-2xl text-[color:var(--dms-navy)]">Sizes & date needed</h2>
              <p className="mt-2 text-sm text-[color:var(--dms-navy)]/60">How many of each size do you need?</p>
              <div className="mt-6 grid grid-cols-3 sm:grid-cols-6 gap-3">
                {SIZES.map((s) => (
                  <div key={s} className="bg-[color:var(--dms-cream-2)] rounded-xl p-3 text-center">
                    <p className="text-xs tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">{s}</p>
                    <input type="number" min="0" value={sizes[s] || ""} onChange={(e) => setSizes({ ...sizes, [s]: e.target.value })} className="mt-2 w-full bg-white rounded-lg border border-[color:var(--dms-navy)]/15 py-2 text-center font-serif text-lg text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)]" placeholder="0" />
                  </div>
                ))}
              </div>
              <p className="mt-4 text-sm text-[color:var(--dms-navy)]/70">Total: <span className="font-serif text-lg text-[color:var(--dms-navy)]">{totalShirts}</span> shirt{totalShirts === 1 ? "" : "s"}</p>

              <div className="mt-8">
                <label className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold flex items-center gap-2"><CalendarIcon className="w-4 h-4" /> Date needed by</label>
                <input type="date" value={dateNeeded} onChange={(e) => setDateNeeded(e.target.value)} className="mt-3 w-full rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 text-[color:var(--dms-navy)] focus:outline-none focus:border-[color:var(--dms-peach)]" />
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-[color:var(--dms-peach)] mx-auto flex items-center justify-center">
                <Check className="w-8 h-8 text-[color:var(--dms-navy)]" />
              </div>
              <h2 className="font-serif text-3xl text-[color:var(--dms-navy)] mt-6">You're all set!</h2>
              <p className="mt-3 text-[color:var(--dms-navy)]/70 max-w-lg mx-auto">We'll review your request and send a proof + exact price to <span className="font-semibold text-[color:var(--dms-navy)]">{email || "you"}</span> within one business day.</p>
              <Link to="/" className="btn-navy mt-8 inline-flex">Back to shop <ArrowRight className="w-4 h-4" /></Link>
            </div>
          )}

          {step >= 1 && step <= 4 && (
            <div className="mt-10 flex items-center justify-between">
              <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="btn-ghost-navy disabled:opacity-40 disabled:cursor-not-allowed">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep(step === 4 ? 4.5 : step + 1)} className="btn-peach">Continue <ArrowRight className="w-4 h-4" /></button>
            </div>
          )}

          {step === 4.5 && (
            <div className="mt-8 border-t border-[color:var(--dms-navy)]/10 pt-8">
              <h3 className="font-serif text-xl text-[color:var(--dms-navy)]">Where should we send your proof?</h3>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 focus:outline-none focus:border-[color:var(--dms-peach)]" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="rounded-xl border border-[color:var(--dms-navy)]/20 bg-white px-4 py-3 focus:outline-none focus:border-[color:var(--dms-peach)]" />
              </div>
              <div className="mt-6 flex items-center justify-end gap-3">
                <button onClick={() => setStep(4)} className="btn-ghost-navy"><ArrowLeft className="w-4 h-4" /> Back</button>
                <button onClick={submit} disabled={!name || !email} className="btn-peach disabled:opacity-40">Send my request <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
